import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {AddItemButton} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-button';
import {TemplatesPopupContent} from '@Components/poster-editor/components/poster-add-item-bar/components/templates-popup-content/templates-popup-content';
import useWindowSize from '@Hooks/useWindowSize';
import {isTemplatesPopupHidden, showTemplatesPopupPulsatingDot} from '@Components/poster-editor/library/poster-editor-library';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {ADD_ITEM_BAR_POPUP_OFFSET} from '@Components/poster-editor/components/poster-add-item-bar/poster-add-item-bar.types';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {updateFeatureToShowOnLoad} from '@Components/poster-editor/poster-editor-reducer';
import {FeatureToShowOnLoad} from '@Components/poster-editor/poster-editor.types';

interface TemplatesDropdownProps {
  popupClassName?: string;
  arrowClasses?: string;
}

const PADDING_AND_NAV_HEIGHT = 88;
const TOP_OFFSET = -16;

export function TemplatesDropdown({popupClassName = '', arrowClasses = ''}: TemplatesDropdownProps): ReactElement<any> | null {
  const [isOpened, setIsOpened] = useState(false);
  const {windowHeight} = useWindowSize();
  const featureToShowOnLoad = useAppSelector((state) => {
    return state.posterEditor.featureToShowOnLoad;
  });
  const dispatch = useAppDispatch();

  const openTemplatesPopup = (): void => {
    if (featureToShowOnLoad !== FeatureToShowOnLoad.TEMPLATE_POPUP) {
      return;
    }

    setIsOpened(true);
  };

  useEffect(() => {
    openTemplatesPopup();
  }, [featureToShowOnLoad]);

  if (isTemplatesPopupHidden()) {
    return null;
  }

  const onAddTemplateCompleted = (): void => {
    dispatch(updateFeatureToShowOnLoad(FeatureToShowOnLoad.NONE));
    trackPosterBuilderGA4Events(GA4EventName.TEMPLATES_MENU_ADD_AFTER_NEW_POSTER);
  };

  const getPopup = (): ReactElement<any> => {
    return <TemplatesPopupContent isContentVisibleToUser={isOpened} onAddTemplateCompleted={onAddTemplateCompleted} />;
  };

  return (
    <Dropdown
      open={isOpened}
      popupSpacingClass="spacing-p-0"
      id="template-dropdown"
      key="template-dropdown"
      alignment={ALIGNMENT_TYPE.RIGHT_END}
      popUpHasCustomHeight
      takeMaxHeight
      maxHeight={windowHeight - PADDING_AND_NAV_HEIGHT}
      topOffset={TOP_OFFSET}
      arrowOffset={TOP_OFFSET * 2}
      popUpHasCustomWidth
      doCloseOnInnerClick={false}
      useDefaultArrow
      arrowClasses={arrowClasses}
      position={DROPDOWN_POSITION.FIXED}
      popupOffset={ADD_ITEM_BAR_POPUP_OFFSET}
      popupClassName={popupClassName}
      onOpen={(): void => {
        if (!isOpened) {
          trackPosterBuilderGA4Events(GA4EventName.TEMPLATES_MENU_OPEN);
          setIsOpened(true);
        }
      }}
      onClose={(): void => {
        if (isOpened) {
          setIsOpened(false);
        }
        if (featureToShowOnLoad === FeatureToShowOnLoad.TEMPLATE_POPUP) {
          trackPosterBuilderGA4Events(GA4EventName.TEMPLATES_MENU_CLOSE_AFTER_NEW_POSTER);
          dispatch(updateFeatureToShowOnLoad(FeatureToShowOnLoad.NONE));
        }
      }}
      popup={getPopup()}
      selector={
        <AddItemButton
          hasPulsatingDot={showTemplatesPopupPulsatingDot()}
          textClasses="capitalize-sentence"
          isSelected={isOpened}
          text={window.i18next.t('pmwjs_templates')}
          icon="icon-multiple-layouts"
        />
      }
      isResumable
    />
  );
}
